import { TranslateModule } from '@ngx-translate/core';
import { BookingChain, BookingChainLink } from '@services/api/booking/booking-chain.model';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

@Component({
  selector: 'app-booking-chain-strip',
  standalone: true,
  imports: [DatePipe, TranslateModule, ButtonModule, TooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="surface-card border-round shadow-2 mb-4 p-4">
      <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
        <span class="flex items-center gap-2 text-sm font-bold text-surface-900">
          <i class="pi pi-link text-primary"></i>
          {{ 'booking.chain.title' | translate }}
          <span class="font-normal text-surface-500">
            {{ 'booking.chain.summary' | translate: { count: chain().links.length, nights: chain().totalNights } }}
          </span>
        </span>
        <p-button
          size="small"
          severity="secondary"
          [outlined]="true"
          icon="pi pi-link"
          [label]="'booking.chain.linkShort' | translate"
          [disabled]="!isCurrentTail() || blocked()"
          [pTooltip]="blocked() ? ('booking.chain.blockedByUnsavedChanges' | translate) : isCurrentTail() ? '' : ('booking.chain.linkOnlyFromTail' | translate)"
          tooltipPosition="left"
          (onClick)="linkRequested.emit()"
        ></p-button>
      </div>

      <div class="flex items-stretch overflow-x-auto pb-1">
        @for (link of chain().links; track link.id; let last = $last; let first = $first) {
          <div
            class="group relative flex-none rounded-lg border p-3 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            [class]="link.isCurrent ? 'border-2 border-primary bg-primary-50 shadow-2' : 'border-surface-200 bg-surface-0 cursor-pointer hover:border-primary-300 hover:shadow-1'"
            style="min-width: 11rem"
            [attr.role]="link.isCurrent ? null : 'button'"
            [attr.tabindex]="link.isCurrent ? null : 0"
            (click)="onLinkClick(link)"
            (keydown.enter)="onLinkClick(link)"
            (keydown.space)="onLinkClick(link); $event.preventDefault()"
          >
            <div class="whitespace-nowrap text-sm font-bold text-surface-900">
              {{ link.startDate | date: 'MM-dd' }} <i class="pi pi-arrow-right mx-1 text-[10px]"></i> {{ link.endDate | date: 'MM-dd' }}
            </div>
            <div class="mt-1 truncate text-xs text-surface-600">{{ link.guestName }}</div>
            <div class="mt-1 text-[11px]" [class]="link.isCurrent ? 'font-bold text-primary' : 'text-surface-500'">
              {{ 'booking.chain.nights' | translate: { count: link.nights } }}
              <span class="mx-1">·</span>
              @if (link.isCurrent) {
                {{ 'booking.chain.current' | translate }}
              } @else {
                #{{ link.id }}
              }
            </div>
            @if (!first && !blocked()) {
              <button
                type="button"
                class="absolute right-1 top-1 hidden rounded p-1 text-surface-400 transition-colors duration-200 hover:bg-surface-100 hover:text-red-600 group-hover:block group-focus-within:block"
                [pTooltip]="'booking.chain.detach' | translate"
                tooltipPosition="top"
                (click)="onDetachClick(link, $event)"
              >
                <i class="pi pi-times text-[11px]"></i>
              </button>
            }
          </div>
          @if (!last) {
            <div class="flex flex-none items-center px-2 text-surface-300">
              <i class="pi pi-arrow-right text-xs"></i>
            </div>
          }
        }
      </div>
    </div>
  `
})
export class BookingChainStripComponent {
  readonly chain = input.required<BookingChain>();
  readonly blocked = input(false);
  readonly linkRequested = output<void>();
  readonly detachRequested = output<BookingChainLink>();
  readonly navigateRequested = output<number>();

  readonly isCurrentTail = computed(() => {
    const links = this.chain().links;
    return links.length > 0 && links[links.length - 1].isCurrent;
  });

  onLinkClick(link: BookingChainLink): void {
    if (link.isCurrent) {
      return;
    }
    this.navigateRequested.emit(link.id);
  }

  onDetachClick(link: BookingChainLink, event: Event): void {
    event.stopPropagation();
    if (this.blocked()) {
      return;
    }
    this.detachRequested.emit(link);
  }
}
